/**
 * Bank Account Service
 * 
 * Service สำหรับจัดการบัญชีธนาคาร
 */

import { queryClass, callCloudFunction } from '@/services/adapters/parse/parse-client'
import { getActiveBanks, findBank, type CurrencyCode, type BankInfo } from '@/data/banks'
import type {
  BankAccount,
  BankAccountQueryParams,
  BankAccountStats,
  BankListItem,
  TransferData,
} from './bank-account.types'

// ============================================
// Mapping Functions
// ============================================

// Fallback image ถ้าหาธนาคารไม่เจอ
const FALLBACK_BANK_IMAGE = '/images/banks/th/undefined.png'

function mapToBankAccount(raw: any): BankAccount {
  // Smart lookup: ลองค้นหาจาก bank_name, bank_id, หรือชื่อธนาคาร
  const bankInfo = findBank(raw.bank_name) || findBank(raw.bank_id) || findBank(raw.banklist?.bank_name)
  
  return {
    id: raw.objectId,
    objectId: raw.objectId,
    name: raw.name || '',
    bank_account: raw.bank_account || '',
    // ใช้ชื่อจาก banks.ts ถ้ามี ไม่งั้นใช้จาก Parse
    bank_name: bankInfo?.name || raw.bank_name || '',
    bank_tag: raw.bank_tag || '',
    bank_id: bankInfo?.code || raw.bank_id || '',
    banklist_sid: raw.banklist_sid || undefined, // ✅ เพิ่ม banklist_sid (19 = TrueWallet)
    // ใช้รูปจาก banks.ts ถ้ามี, fallback เป็น undefined.png
    image_url: bankInfo?.image || raw.image_url || FALLBACK_BANK_IMAGE,
    // เพิ่ม bank color
    bank_color: bankInfo?.color || '#6b7280',
    type: raw.type || 'Deposit',
    status: raw.status || 'Inactive',
    api_status: raw.api_status || 'suspend',
    amount: raw.amount || 0,
    primaryDeposit: raw.primaryDeposit || false,
    primaryWithdraw: raw.primaryWithdraw || false,
    bankMaintenance: raw.bankMaintenance || false,
    maintenanceMessage: raw.maintenanceMessage || '',
    bankApi: raw.bankApi || false,
    slipApi: raw.slipApi || false,
    lineconnectApi: raw.lineconnectApi || false,
    gatewayApi: raw.gatewayApi || false,
    show_transfer: raw.show_transfer || false,
    seq: raw.seq || 0,
    access_key: raw.access_key || null,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
  }
}

function mapToBankListItem(raw: any): BankListItem {
  return {
    id: raw.objectId,
    bank_name: raw.bank_name || '',
    bank_id: raw.bank_id || '',
    image_url: raw.image_url || '',
    status: raw.status || 'Inactive',
  }
}

// ============================================
// Service
// ============================================

export const BankAccountService = {
  /**
   * ดึงรายการบัญชีธนาคารทั้งหมด
   */
  async getAll(
    sessionToken: string,
    params?: BankAccountQueryParams
  ): Promise<BankAccount[]> {
    console.log('📊 [BankAccountService] getAll:', params)

    const where: Record<string, any> = {
      deletedAt: { $exists: false },
    }

    if (params?.status) {
      where.status = params.status
    }

    if (params?.type) {
      where.type = params.type
    }

    const result = await queryClass('WebBankAccount', {
      where,
      order: 'seq',
      limit: params?.limit || 100,
    }, sessionToken)

    console.log('📊 [BankAccountService] Found', result.results?.length, 'accounts')
    
    return (result.results || []).map(mapToBankAccount)
  },

  /**
   * ดึงบัญชีธนาคารตาม ID
   */
  async getById(
    sessionToken: string,
    id: string
  ): Promise<BankAccount | null> {
    console.log('📊 [BankAccountService] getById:', id)

    const result = await queryClass('WebBankAccount', {
      where: {
        objectId: id,
        deletedAt: { $exists: false },
      },
      limit: 1,
    }, sessionToken)

    if (result.results?.length > 0) {
      return mapToBankAccount(result.results[0])
    }
    return null
  },

  /**
   * ดึงรายชื่อธนาคาร (BankList) จาก Parse Server
   */
  async getBankList(sessionToken: string): Promise<BankListItem[]> {
    console.log('📊 [BankAccountService] getBankList from Parse')

    const result = await queryClass('BankList', {
      where: {
        status: 'Active',
        deletedAt: { $exists: false },
      },
      order: 'bank_name',
      limit: 100,
    }, sessionToken)

    return (result.results || []).map(mapToBankListItem)
  },

  /**
   * ดึงรายชื่อธนาคารจาก Local Data (ไม่ต้อง API)
   * ใช้สำหรับ dropdown และ UI
   * 
   * @param currency - รหัสสกุลเงิน (THB, LAK, KHR)
   * @returns BankListItem[] - รายชื่อธนาคาร
   */
  getBankListLocal(currency: CurrencyCode = 'THB'): BankListItem[] {
    console.log('📊 [BankAccountService] getBankList from Local Data, currency:', currency)
    
    const banks = getActiveBanks(currency)
    
    return banks.map((bank: BankInfo) => ({
      id: bank.id,                    // '1', '2', '3'... (ตัวเลข string)
      bank_name: bank.name,           // ชื่อธนาคาร
      bank_id: bank.bankId || bank.code, // Parse objectId หรือ code
      image_url: bank.image,          // /images/banks/1.png
      status: 'Active',
    }))
  },

  /**
   * คำนวณสถิติบัญชีธนาคาร
   */
  calculateStats(accounts: BankAccount[]): BankAccountStats {
    const depositAccounts = accounts.filter(a => a.type === 'Deposit' || a.type === 'Both')
    const withdrawAccounts = accounts.filter(a => a.type === 'Withdraw' || a.type === 'Both')

    return {
      totalAccounts: accounts.length,
      depositAccounts: depositAccounts.length,
      withdrawAccounts: withdrawAccounts.length,
      totalBalance: accounts.reduce((sum, a) => sum + (a.amount || 0), 0),
      depositBalance: depositAccounts.reduce((sum, a) => sum + (a.amount || 0), 0),
      withdrawBalance: withdrawAccounts.reduce((sum, a) => sum + (a.amount || 0), 0),
    }
  },

  /**
   * เปิด/ปิด Primary Deposit
   */
  async togglePrimaryDeposit(
    _sessionToken: string,
    id: string
  ): Promise<void> {
    console.log('📊 [BankAccountService] togglePrimaryDeposit:', id)
    try {
      await callCloudFunction('activePrimaryDepositWebBankAccount', { id })
    } catch (error: any) {
      console.error('❌ [BankAccountService] togglePrimaryDeposit error:', error)
      throw new Error(error.message || 'เกิดข้อผิดพลาดในการตั้งบัญชีฝากหลัก')
    }
  },

  /**
   * เปิด/ปิด Primary Withdraw
   */
  async togglePrimaryWithdraw(
    _sessionToken: string,
    id: string
  ): Promise<void> {
    console.log('📊 [BankAccountService] togglePrimaryWithdraw:', id)
    try {
      await callCloudFunction('activePrimaryWithdrawWebBankAccount', { id })
    } catch (error: any) {
      console.error('❌ [BankAccountService] togglePrimaryWithdraw error:', error)
      throw new Error(error.message || 'เกิดข้อผิดพลาดในการตั้งบัญชีถอนหลัก')
    }
  },

  /**
   * เปิด/ปิด API Status
   */
  async toggleApiStatus(
    _sessionToken: string,
    id: string
  ): Promise<boolean> {
    console.log('📊 [BankAccountService] toggleApiStatus:', id)
    try {
      await callCloudFunction('activeWebBankAccount', { id })
      return true
    } catch (error) {
      console.error('❌ [BankAccountService] toggleApiStatus error:', error)
      return false
    }
  },

  /**
   * ลบบัญชีธนาคาร (Soft delete)
   */
  async delete(
    _sessionToken: string,
    id: string
  ): Promise<boolean> {
    console.log('📊 [BankAccountService] delete:', id)
    try {
      await callCloudFunction('deleteWebBankAccount', { id })
      return true
    } catch (error) {
      console.error('❌ [BankAccountService] delete error:', error)
      return false
    }
  },

  /**
   * โอนเงินระหว่างบัญชี
   */
  async transfer(
    _sessionToken: string,
    data: TransferData
  ): Promise<boolean> {
    console.log('📊 [BankAccountService] transfer:', data)
    try {
      await callCloudFunction('transferWebBankAccount', { data })
      return true
    } catch (error) {
      console.error('❌ [BankAccountService] transfer error:', error)
      return false
    }
  },

  /**
   * บันทึกบัญชีธนาคาร (สร้างใหม่/อัพเดท)
   */
  async save(
    _sessionToken: string,
    data: any
  ): Promise<{ success: boolean; objectId?: string; error?: string }> {
    console.log('📊 [BankAccountService] save:', data)
    try {
      const result = await callCloudFunction('saveWebBankAccount', { data })
      return { success: true, objectId: result.objectId }
    } catch (error: any) {
      console.error('❌ [BankAccountService] save error:', error)
      return { success: false, error: error.message || 'เกิดข้อผิดพลาด' }
    }
  },

  /**
   * เปิด/ปิด Maintenance Mode
   */
  async toggleMaintenance(
    _sessionToken: string,
    id: string,
    enabled: boolean,
    message?: string
  ): Promise<boolean> {
    console.log('📊 [BankAccountService] toggleMaintenance:', { id, enabled, message })
    try {
      await callCloudFunction('toggleMaintenanceWebBankAccount', { id, enabled, message })
      return true
    } catch (error) {
      console.error('❌ [BankAccountService] toggleMaintenance error:', error)
      return false
    }
  },

  /**
   * อัพเดทสถานะบัญชี (Active/Inactive)
   */
  async updateStatus(
    _sessionToken: string,
    id: string,
    status: 'Active' | 'Inactive'
  ): Promise<boolean> {
    console.log('📊 [BankAccountService] updateStatus:', { id, status })
    try {
      await callCloudFunction('updateStatusWebBankAccount', { id, status })
      return true
    } catch (error) {
      console.error('❌ [BankAccountService] updateStatus error:', error)
      return false
    }
  },
}

export default BankAccountService

